import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Plus, Edit2, Trash2, Save, X, Star, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import BusinessHoursInput from '@/components/BusinessHoursInput';

const API = `${window.location.origin}/api`;

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DEFAULT_HOURS = {
  mon: { open: '09:00', close: '17:00', closed: false },
  tue: { open: '09:00', close: '17:00', closed: false },
  wed: { open: '09:00', close: '17:00', closed: false },
  thu: { open: '09:00', close: '17:00', closed: false },
  fri: { open: '09:00', close: '17:00', closed: false },
  sat: { open: '10:00', close: '16:00', closed: false },
  sun: { open: '00:00', close: '00:00', closed: true }
};

function LocationForm({ location, onSave, onCancel, saving }) {
  const [formData, setFormData] = useState({
    name: location?.name || '',
    address: location?.address || '',
    city: location?.city || '',
    state: location?.state || '',
    zip_code: location?.zip_code || '',
    phone: location?.phone || '',
    business_hours: location?.business_hours || DEFAULT_HOURS,
    is_primary: location?.is_primary || false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.address || !formData.city || !formData.state) {
      toast.error('Address, city, and state are required');
      return;
    }
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label className="text-zinc-400 text-sm">Location Name (Optional)</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Downtown Branch"
            className="bg-zinc-900 border-zinc-800 text-white mt-1"
          />
        </div>

        <div className="col-span-2">
          <Label className="text-zinc-400 text-sm">Street Address *</Label>
          <Input
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            placeholder="123 Main Street"
            className="bg-zinc-900 border-zinc-800 text-white mt-1"
            required
          />
        </div>

        <div>
          <Label className="text-zinc-400 text-sm">City *</Label>
          <Input
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            placeholder="Atlanta"
            className="bg-zinc-900 border-zinc-800 text-white mt-1"
            required
          />
        </div>

        <div>
          <Label className="text-zinc-400 text-sm">State *</Label>
          <Input
            value={formData.state}
            onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
            placeholder="GA"
            maxLength={2}
            className="bg-zinc-900 border-zinc-800 text-white mt-1"
            required
          />
        </div>

        <div>
          <Label className="text-zinc-400 text-sm">ZIP Code</Label>
          <Input
            value={formData.zip_code}
            onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
            placeholder="30301"
            className="bg-zinc-900 border-zinc-800 text-white mt-1"
          />
        </div>

        <div>
          <Label className="text-zinc-400 text-sm">Phone</Label>
          <Input
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="(555) 123-4567"
            className="bg-zinc-900 border-zinc-800 text-white mt-1"
          />
        </div>
      </div>

      <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/50 border border-zinc-800">
        <div>
          <p className="text-white font-medium">Primary Location</p>
          <p className="text-zinc-500 text-xs">This will be the main address shown</p>
        </div>
        <Switch
          checked={formData.is_primary}
          onCheckedChange={(checked) => setFormData({ ...formData, is_primary: checked })}
        />
      </div>

      <div className="border-t border-zinc-800 pt-4">
        <BusinessHoursInput
          value={formData.business_hours}
          onChange={(hours) => setFormData({ ...formData, business_hours: hours })}
        />
      </div>

      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 py-2 rounded-lg bg-white text-black font-medium hover:bg-zinc-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Location'}
        </button>
      </div>
    </form>
  );
}

export default function LocationsManager({ businessId, businessName }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, [businessId]);

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${API}/businesses/${businessId}/locations`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLocation = async (data) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/businesses/${businessId}/locations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      });

      if (response.ok) {
        toast.success('Location added successfully');
        setShowAddDialog(false);
        fetchLocations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add location');
      }
    } catch (error) {
      toast.error('Error adding location');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateLocation = async (data) => {
    if (!editingLocation) return;
    
    setSaving(true);
    try {
      const response = await fetch(
        `${API}/businesses/${businessId}/locations/${editingLocation.location_id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(data)
        }
      );

      if (response.ok) {
        toast.success('Location updated successfully');
        setEditingLocation(null);
        fetchLocations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update location');
      }
    } catch (error) {
      toast.error('Error updating location');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!confirm('Are you sure you want to delete this location?')) return;

    try {
      const response = await fetch(
        `${API}/businesses/${businessId}/locations/${locationId}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        toast.success('Location deleted');
        fetchLocations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete location');
      }
    } catch (error) {
      toast.error('Error deleting location');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-24 skeleton rounded-xl" />
        <div className="h-24 skeleton rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-white">Locations</h2>
          <p className="text-zinc-500 text-sm">Manage multiple locations for {businessName}</p>
        </div>
        <button
          onClick={() => setShowAddDialog(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-black font-medium hover:bg-zinc-200 transition-colors"
          data-testid="add-location-btn"
        >
          <Plus className="w-4 h-4" />
          Add Location
        </button>
      </div>

      {/* Locations List */}
      {locations.length > 0 ? (
        <div className="space-y-4">
          {locations.map((location, index) => (
            <motion.div
              key={location.location_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="glass-card rounded-xl p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-white">
                        {location.name || `Location ${index + 1}`}
                      </h3>
                      {location.is_primary && (
                        <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 text-xs">
                          Primary
                        </span>
                      )}
                    </div>
                    <p className="text-zinc-400 text-sm">
                      {location.address}, {location.city}, {location.state} {location.zip_code}
                    </p>
                    {location.phone && (
                      <p className="text-zinc-500 text-sm mt-1">{location.phone}</p>
                    )}
                    
                    {/* Rating */}
                    {location.review_count > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <span className="text-white text-sm">{location.average_rating?.toFixed(1)}</span>
                        <span className="text-zinc-500 text-sm">({location.review_count} reviews)</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditingLocation(location)}
                    className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteLocation(location.location_id)}
                    className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="glass-card rounded-xl p-12 text-center">
          <MapPin className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No Additional Locations</h3>
          <p className="text-zinc-500 mb-6">
            Add more locations if your business operates from multiple addresses.
          </p>
          <button
            onClick={() => setShowAddDialog(true)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Your First Location
          </button>
        </div>
      )}

      {/* Add Location Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#0F0F0F] border border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Add New Location</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Add another location for {businessName}
            </DialogDescription>
          </DialogHeader>
          <LocationForm
            onSave={handleAddLocation}
            onCancel={() => setShowAddDialog(false)}
            saving={saving}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Location Dialog */}
      <Dialog open={!!editingLocation} onOpenChange={() => setEditingLocation(null)}>
        <DialogContent className="bg-[#0F0F0F] border border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Location</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Update this location's details
            </DialogDescription>
          </DialogHeader>
          {editingLocation && (
            <LocationForm
              location={editingLocation}
              onSave={handleUpdateLocation}
              onCancel={() => setEditingLocation(null)}
              saving={saving}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
